import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from 'src/components/atoms/button'
import Dropdown from 'src/components/atoms/dropdown'
import IconButton from 'src/components/atoms/iconButton'
import Popconfirm from 'src/components/atoms/popconfirm'
import Tooltip from 'src/components/atoms/tooltip'
import { FullScreenModal } from 'src/components/modals/fullScreenModal'

import {
    Action,
    Actions,
    ButtonAction as ButtonActionType,
    ButtonConfirmAction as ButtonConfirmActionType,
    Confirmable,
    FullScreenTriggerAction as FullScreenTriggerActionType,
    IconAction as IconActionType,
    IconConfirmAction as IconConfirmActionType,
    MoreAction as MoreActionType,
} from './types'

import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Space } from 'antd'

const ICON_SIZE = 'sm'

interface Props {
    actions: Actions
    className?: string
}

// Not creating a general "More" icon component, as this should be the only place it's used
const MoreIcon = () => <FontAwesomeIcon size={ICON_SIZE} icon={faEllipsisVertical} />

const ActionToolbar = (props: Props) => {
    const { actions, className } = props

    const sanitizedActions = actions?.filter(Boolean)

    if (!sanitizedActions?.length) {
        return null
    }

    return (
        <Space className={className}>
            {sanitizedActions.map((action, index) => (
                <div key={index}>{renderAction(action)}</div>
            ))}
        </Space>
    )
}

const renderAction = (action: Action) => {
    switch (action.type) {
        case 'icon':
            return <IconAction {...action} />
        case 'button':
            return <ButtonAction {...action} />
        case 'fullscreenTrigger':
            return <FullScreenTriggerAction {...action} />
        case 'more':
            return <MoreAction {...action} />
    }
}

const IconAction = (action: IconActionType | IconConfirmActionType) => {
    const { label, icon, disabled, onClick, tooltip } = action

    const { t } = useTranslation()

    const Icon = icon

    const iconButton = (
        <IconButton
            shape="default"
            disabled={disabled}
            icon={<Icon size={ICON_SIZE} />}
            // Prevents a native tooltip showing up next to the rendered one
            title={tooltip ? undefined : label}
            aria-label={label}
            onClick={hasConfirmProps(action) ? undefined : onClick}
        />
    )

    const actionButton = hasConfirmProps(action) ? (
        <Popconfirm placement="bottom" cancelText={t('Cancel')} {...action.confirmProps}>
            {iconButton}
        </Popconfirm>
    ) : (
        iconButton
    )

    if (tooltip) {
        return <Tooltip title={tooltip}>{actionButton}</Tooltip>
    }

    return actionButton
}

const ButtonAction = (action: ButtonActionType | ButtonConfirmActionType) => {
    const {
        label,
        title,
        variant = 'default',
        disabled,
        danger,
        loading,
        onClick,
        icon,
        iconPosition = 'start',
        tooltip,
    } = action
    const Icon = icon
    const iconElement = icon ? <Icon size={ICON_SIZE} /> : null
    const { t } = useTranslation()

    // A disabled button swallows pointer events, which would keep its tooltip from ever opening
    const buttonStyle = disabled && tooltip ? { pointerEvents: 'none' as const } : undefined

    if (hasConfirmProps(action)) {
        const { confirmProps } = action

        const actionButton = (
            <Popconfirm placement="bottom" cancelText={t('Cancel')} {...confirmProps}>
                <Button
                    type={variant}
                    disabled={disabled}
                    title={title ?? label}
                    danger={danger}
                    loading={loading}
                    icon={iconElement}
                    iconPosition={iconPosition}
                    style={buttonStyle}
                >
                    {label}
                </Button>
            </Popconfirm>
        )

        if (tooltip) {
            return <Tooltip title={tooltip}>{actionButton}</Tooltip>
        }

        return actionButton
    }

    const actionButton = (
        <Button
            type={variant}
            disabled={disabled}
            title={title ?? label}
            danger={danger}
            loading={loading}
            onClick={onClick}
            icon={iconElement}
            iconPosition={iconPosition}
            style={buttonStyle}
        >
            {label}
        </Button>
    )

    if (tooltip) {
        return <Tooltip title={tooltip}>{actionButton}</Tooltip>
    }

    return actionButton
}

const FullScreenTriggerAction = (action: FullScreenTriggerActionType) => {
    const { label, disabled, fullScreenModalProps } = action
    const { title, content, cancelText, okText } = fullScreenModalProps

    const { t } = useTranslation()

    const [open, setOpen] = useState(false)

    return (
        <>
            <Button disabled={disabled} title={label} onClick={() => setOpen(true)}>
                {label}
            </Button>
            <FullScreenModal
                hideCloseButton
                open={open}
                title={title}
                onClose={() => setOpen(false)}
                actions={[
                    { type: 'button', label: cancelText ?? t('Cancel'), onClick: () => setOpen(false) },
                    { type: 'button', variant: 'primary', label: okText ?? t('Save'), onClick: () => setOpen(false) },
                ]}
            >
                {content}
            </FullScreenModal>
        </>
    )
}

const MoreAction = (action: MoreActionType) => {
    const { label, disabled, buttonType = 'text', children } = action

    const { t } = useTranslation()

    const [confirmChildIndex, setConfirmChildIndex] = useState<number>()

    const confirmChild = confirmChildIndex === undefined ? undefined : children[confirmChildIndex]
    const confirmProps = confirmChild && hasConfirmProps(confirmChild) ? confirmChild.confirmProps : undefined

    const menuItems = children.map((child, index) => {
        const Icon = child.icon

        return {
            key: index.toString(),
            disabled: child.disabled,
            label: child.label,
            icon: Icon ? <Icon fixedWidth style={{ fontSize: '1.25em' /* Default size is too small */ }} /> : undefined,
            onClick: () => {
                if (hasConfirmProps(child)) {
                    setConfirmChildIndex(index)
                } else {
                    child.onClick()
                }
            },
        }
    })

    // Keeps clicks on the menu and the confirm from reaching clickable parents, like table rows
    return (
        <div onClick={(e) => e.stopPropagation()}>
            <Popconfirm
                open={!!confirmProps}
                placement="bottom"
                title={confirmProps?.title}
                {...confirmProps}
                cancelText={t('Cancel')}
                onCancel={(e) => {
                    setConfirmChildIndex(undefined)
                    confirmProps?.onCancel?.(e)
                }}
                onConfirm={(e) => {
                    setConfirmChildIndex(undefined)
                    confirmProps?.onConfirm?.(e)
                }}
            >
                <Dropdown disabled={disabled} trigger={['click']} menu={{ items: menuItems }} placement="bottomRight">
                    <IconButton
                        className="text-black-500"
                        title={label}
                        icon={<MoreIcon />}
                        disabled={disabled}
                        type={buttonType}
                    />
                </Dropdown>
            </Popconfirm>
        </div>
    )
}

const hasConfirmProps = <T,>(action: T): action is T & Confirmable => {
    return action.hasOwnProperty('confirmProps')
}

export default ActionToolbar
